import { supabase } from "@/integrations/supabase/client";
import { defaultTemplates } from "./defaultEmailTemplates";

/**
 * Send a template-based email.
 * Fetches the template from DB (or falls back to defaults),
 * replaces variables, and invokes the send-email Edge Function.
 */
export async function sendTemplateEmail(
  orgId: string,
  templateType: string,
  to: string,
  variables: Record<string, string>,
  replyTo?: string
): Promise<boolean> {
  try {
    // 1. Fetch template from DB
    const { data: dbTemplate } = await supabase
      .from("email_templates")
      .select("subject, body, is_active")
      .eq("organization_id", orgId)
      .eq("type", templateType)
      .maybeSingle();

    // Use DB template if active, otherwise fall back to default
    const template =
      dbTemplate && dbTemplate.is_active
        ? { subject: dbTemplate.subject, body: dbTemplate.body }
        : defaultTemplates[templateType];

    if (!template) {
      console.warn(`[sendTemplateEmail] No template for type: ${templateType}`);
      return false;
    }

    // 2. Replace variables
    let subject = template.subject;
    let body = template.body;
    for (const [key, value] of Object.entries(variables)) {
      const re = new RegExp(`\\{${key}\\}`, "g");
      subject = subject.replace(re, value);
      body = body.replace(re, value);
    }

    // 3. Get org name for from_name
    const { data: org } = await supabase
      .from("organizations")
      .select("name, contact_email")
      .eq("id", orgId)
      .single();

    // 4. Send via Edge Function
    const { error } = await supabase.functions.invoke("send-email", {
      body: {
        to,
        subject,
        body,
        from_name: org?.name ?? "Boply",
        reply_to: replyTo ?? org?.contact_email ?? undefined,
      },
    });

    if (error) {
      console.error("[sendTemplateEmail] Error:", error);
      return false;
    }
    return true;
  } catch (err) {
    console.error("[sendTemplateEmail] Error:", err);
    return false;
  }
}
